import React from "react";
import Highcharts from "highcharts";
import HighchartsTreemap from "highcharts/modules/treemap";
import HighchartsReact from "highcharts-react-official";
import { useSelector } from "react-redux";
import useStyles, { aboutDrawerWidth } from "./Styles";
import { Box, Slider, Tooltip } from "@material-ui/core";
import { toolbarHeight } from "components/app/Styles";
import {
  aboutTogglebarWidth,
  filterContainerHeight,
  chartTitleHeight,
  chartTogglebarWidth,
} from "./Styles";
import useGetViewPortDimensions from "hooks/useGetViewPortDimensions";

HighchartsTreemap(Highcharts);

const getWindowSize = () => {
  const { innerWidth, innerHeight } = window;
  return { innerWidth, innerHeight };
};

const Treemap = () => {
  const { toggle_about } = useSelector((state) => state.central_arizona);
  const { entitlements, scenario } = useSelector(
    (state) => state.central_arizona
  );
  const classes = useStyles();
  const indian_communities = [
    "Gila River Indian Community",
    "Ak-Chin Indian Community",
    "Fort McDowell Indian Community",
    "Tohono O'odham",
    "Salt River Pima-Maricopa Indian Community",
    "San Carlos Apache Tribe",
    "Pascua Yaqui Tribe",
    "Camp Verde (Yavapai-Apache)",
    "Tonto Apache Tribe",
  ];
  const colors = [
    "#0C1B5F",
    "#2352D0",
    "#6AA4DC",
    "#96D9E4",
    "#F3C745",
    "#D88C31",
    "#DE3E40",
    "#AF1D1F",
    "#203864",
    "#2F5597",
    "#800080",
    "#6398D9",
    "#A8C7ED",
    "#9191E2",
    "#5252E2",
  ];
  const [windowSize, setWindowSize] = React.useState(getWindowSize());
  const [availableWater, setAvailableWater] = React.useState(0);
  const [options, setOptions] = React.useState(null);
  const [waters, setWaters] = React.useState(scenario === 'DCP Cuts' ? [
    1300000, 1000000, 900000, 850000, 800000,
  ] : [700000, 600000, 500000, 400000, 300000]);
  const [marks, setMarks] = React.useState([
    { value: 0, label: "Tier 1" },
    { value: 1, label: "Tier 2" },
    { value: 2, label: "Tier 3" },
    { value: 3, label: "Tier 4" },
    { value: 4, label: "Tier 5" },
  ]);
  const { isMobile } = useGetViewPortDimensions();

  const valueLabelFormat = (value) => {
    // Handles the case when the value has already been converted to the string format
    if (typeof value !== "number") {
      return value;
    }
    return `${parseInt(waters[value]).toLocaleString("en-US")} acre-feet`;
  };

  const ValueLabelComponent = (props) => {
    const classes = useStyles();
    const { children, value } = props;
    const title = value;
    return (
      <Tooltip
        placement="top"
        arrow
        title={title}
        open={true}
        classes={{
          tooltip: classes.cityTooltipPopper,
        }}
      >
        {children}
      </Tooltip>
    );
  };

  React.useEffect(() => {
    const handleWindowResize = () => {
      setWindowSize(getWindowSize());
    };
    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  const scenarioLifeCycle = () => {
    let ticks = [];
    let water = [];
    let num_waters = Object.keys(entitlements[scenario]).length;
    Object.keys(entitlements[scenario]).map((water_available, idx) => {
      let total_water = water_available;
      // let cnt = 0;
      while (total_water >= 1000) {
        total_water /= 1000;
        // cnt++;
      }
      const tierMapping = {
        1: "Tier 0",
        2: "Tier 1",
        3: "Tier 2a",
        4: "Tier 2b",
        5: "Tier 3",
      };
      ticks.push({
        value: num_waters - idx - 1,
        label:
          scenario === "DCP Cuts"
            ? `${tierMapping[num_waters - idx]}`
            : `${water_available / 1000}K`,
      });

      water.push(water_available);
      return water_available
    });
    water = water.reverse();
    setMarks(ticks);
    setWaters(water);
  }

  React.useEffect(scenarioLifeCycle, [scenario]);

  const updateChart = () => {
    let options_temp = {
      chart: {
        backgroundColor: "transparent",
        width: isMobile ? 380 :
          windowSize.innerWidth -
          chartTogglebarWidth -
          (toggle_about ? aboutDrawerWidth : aboutTogglebarWidth) -
          80, // 80 for padding
        maxWidth: isMobile ? 390 :
          windowSize.innerWidth -
          chartTogglebarWidth -
          aboutTogglebarWidth -
          100,
        height:
          windowSize.innerHeight -
          toolbarHeight -
          filterContainerHeight -
          chartTitleHeight -
          150,
      },
      credits: {
        enabled: false,
      },
      series: [
        {
          type: "treemap",
          layoutAlgorithm: "squarified",
          dataLabels: {
            align: "left",
            verticalAlign: "top",
            paddingTop: 12,
            paddingLeft: 12,
            color: "#FFF",
            style: {
              fontSize: 14,
              lineHeight: 18,
              fontFamily: "'Arial', sans-serif",
              fontWeight: "normal",
              textOutline: 0,
            },
          },
          data: [],
        },
      ],
      title: { style: { display: "none" } },
      tooltip: {
        padding: 16,
        backgroundColor: "#FFF",
        borderRadius: 8,
        useHTML: true,
        style: { color: "#000", fontFamily: "'Arial', sans-serif" },
        formatter() {
          const index = this.point.index;
          const p13_contract_entitlement =
            (entitlements[scenario][waters[availableWater]][index]
              .p13_contract_entitlement /
              entitlements[scenario][waters[availableWater]][index]
                .total_available) *
            100;
          const mi_priority_contract_entitlement =
            (entitlements[scenario][waters[availableWater]][index]
              .mi_priority_contract_entitlement /
              entitlements[scenario][waters[availableWater]][index]
                .total_available) *
            100;
          const indian_priority_contract_entitlement =
            (entitlements[scenario][waters[availableWater]][index]
              .indian_priority_contract_entitlement /
              entitlements[scenario][waters[availableWater]][index]
                .total_available) *
            100;
          const nia_priority_contract_entitlement =
            (entitlements[scenario][waters[availableWater]][index]
              .nia_priority_contract_entitlement /
              entitlements[scenario][waters[availableWater]][index]
                .total_available) *
            100;
          const leases_exchanges =
            (entitlements[scenario][waters[availableWater]][index]
              .leases_exchanges /
              entitlements[scenario][waters[availableWater]][index]
                .total_available) *
            100;
          const leased_out =
            (entitlements[scenario][waters[availableWater]][index].obligation /
              entitlements[scenario][waters[availableWater]][index]
                .total_available) *
            100;
          const entity =
            entitlements[scenario][waters[availableWater]][index].entity;
          let tooltip = "";
          if (indian_communities.includes(entity)) {
            tooltip += `<strong>Remaining access (${entity})</strong>`;
            tooltip += `<p>${entitlements[scenario][waters[availableWater]][
              index
            ].total_available.toLocaleString("en-US")} acre-feet</p>`;
          } else {
            tooltip += `<strong>Percent access by priority pool (${entity})</strong>`;
            if (p13_contract_entitlement > 0)
              tooltip +=
                "<p>P3 Contract Entitlement: " +
                p13_contract_entitlement.toFixed(2) +
                "%</p>";
            if (mi_priority_contract_entitlement > 0)
              tooltip +=
                "<p>M&I Priority Contract Entitlement: " +
                mi_priority_contract_entitlement.toFixed(2) +
                "%</p>";
            if (indian_priority_contract_entitlement > 0)
              tooltip +=
                "<p>Indian Priority Contract Entitlement: " +
                indian_priority_contract_entitlement.toFixed(2) +
                "%</p>";
            if (nia_priority_contract_entitlement > 0)
              tooltip +=
                "<p>NIA Priority Contract Entitlement: " +
                nia_priority_contract_entitlement.toFixed(2) +
                "%</p>";
            if (leases_exchanges !== 0)
              tooltip +=
                "<p>Leases & Exchanges: " +
                leases_exchanges.toFixed(2) +
                "%</p>";
            if (leased_out !== 0)
              tooltip +=
                "<p>Leases & Exchanges: " + leased_out.toFixed(2) + "%</p>";
          }
          return tooltip;
        },
      },
    };

    // console.log(scenario, waters, availableWater, entitlements);
    // Sorts the entitlements based on total access to colorado river water
    entitlements[scenario][waters[availableWater]].sort(function compare(a, b) {
      if (a.total_available > b.total_available) {
        return -1;
      } else if (a.total_available < b.total_available) {
        return 1;
      }
      return 0;
    });

    options_temp.series[0].data = []; 
    
    entitlements[scenario][waters[availableWater]].map((entitlement, idx) => {
      if (idx < 15)
        options_temp.series[0].data.push({
          name: entitlement.entity,
          value: entitlement.total_available,
          color: colors[idx],
          states: {
            hover: { fill: colors[idx] },
          },
        });
      return entitlement
    });
    setOptions(options_temp);
  };

  React.useEffect(updateChart, [waters, availableWater, windowSize, toggle_about]);

  const handleSliderChange = (e, value) => {
    setAvailableWater(value);
  };

  return (
    <Box className={classes.chartContainer}>
      <Box className={classes.sliderContainer}>
        <Slider
          defaultValue={availableWater}
          step={1}
          marks={marks}
          min={0}
          max={4}
          classes={{
            root: classes.citySlider,
            rail: classes.citySliderRail,
            track: classes.citySliderTrack,
            valueLabel: classes.sliderValueLabel,
            thumb: classes.citySliderThumb,
            mark: classes.citySliderMark,
            markLabel: classes.cityMarkLabel,
          }}
          onChangeCommitted={handleSliderChange}
          valueLabelDisplay="on"
          getAriaValueText={valueLabelFormat}
          valueLabelFormat={valueLabelFormat}
          ValueLabelComponent={ValueLabelComponent}
        />
      </Box>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Box>
  );
};

export default Treemap;
